/* eslint-disable eslint-comments/no-restricted-disable -- Need to disable for now */

/* eslint-disable sonarjs/cognitive-complexity -- Not much to refactor at the moment */
import produce from "immer";
import type { StateCreator } from "zustand";
import { type SiteTheme } from "../SiteThemes/constants";
import type { Middlewares, Mutators } from "./index";
import type {
  BuilderUIState,
  CustomColors,
  InitialData,
  InterfaceState,
} from "./types";

export type InterfaceSlice = StateCreator<
  BuilderUIState,
  Middlewares,
  Mutators,
  InterfaceState
>;

type BuilderSetState = Parameters<InterfaceSlice>[0];
type BuilderGetState = Parameters<InterfaceSlice>[1];

export type Appearance = "light" | "dark";

const createZoomSlice = (set: BuilderSetState, get: BuilderGetState) => ({
  level: 1,
  minLevel: 0.25,
  maxLevel: 1,
  steps: [0.25, 0.5, 0.75, 1],
  zoomIn: () => {
    const { zoom } = get();
    const { maxLevel } = zoom;
    if (zoom.level > maxLevel) return;

    const currentIndex = zoom.steps.findIndex(
      (step: number) => step === zoom.level,
    );
    const nextLevel = zoom.steps[currentIndex + 1] ?? zoom.level;

    set({ zoom: { ...zoom, level: nextLevel } }, undefined, "zoom/zoomIn");
  },
  zoomOut: () => {
    const { zoom } = get();
    const { minLevel } = zoom;
    if (zoom.level < minLevel) return;

    const currentIndex = zoom.steps.findIndex(
      (step: number) => step === zoom.level,
    );
    const nextLevel = zoom.steps[currentIndex - 1] ?? zoom.level;

    set({ zoom: { ...zoom, level: nextLevel } }, undefined, "zoom/zoomOut");
  },
  updateLevel: (level: number) => {
    const { zoom } = get();
    const defaultSteps = [0.25, 0.5, 0.75, 1];
    const newSteps = defaultSteps.filter(step => step < level);

    set(
      {
        zoom: {
          ...zoom,
          level,
          minLevel: newSteps[0],
          maxLevel: level,
          steps: [...newSteps, level],
        },
      },
      undefined,
      "zoom/updateLevel",
    );
  },
});

const createThemeUpdater = (set: BuilderSetState, get: BuilderGetState) => ({
  updateCurrentCanvasThemeVariables: (
    key: string,
    variableName: string,
    value: string,
  ) => {
    const { currentCanvasTheme } = get();

    if (!isValidThemeUpdate(currentCanvasTheme, key)) return;

    set(
      produce((state: BuilderUIState) => {
        state.currentCanvasTheme.cssVariables[key][variableName] = value;
      }),
      undefined,
      "theme/updateCurrentCanvasThemeVariables",
    );
  },
  updateCustomColors: (colors: CustomColors) => {
    const { currentCanvasTheme } = get();

    if (!currentCanvasTheme) {
      console.warn("No currentCanvasTheme is set.");
      return;
    }

    set(
      produce((state: BuilderUIState) => {
        state.currentCanvasTheme.customColors = colors;
      }),
      undefined,
      "theme/updateCustomColors",
    );
  },
});

export const createInterfaceSlice =
  (initialData: InitialData): InterfaceSlice =>
  (set, get) => ({
    actionSidebarView: "",
    canvasSize: "desktop",
    appearance: initialData.appearance ?? "light",
    currentCanvasTheme: initialData.theme,
    isFloatingDropZonesEnabled: initialData.isFloatingDropZonesEnabled ?? false,
    shouldAllowInlineEditing: initialData.shouldAllowInlineEditing ?? false,
    dropZonePositions: [],
    closeSidebarView: () =>
      set(
        { isActionSidebarOpen: false, actionSidebarView: undefined },
        undefined,
        "interface/closeSidebarView",
      ),
    enterFullscreenPreview: () =>
      set(
        { isFullscreenPreview: true },
        undefined,
        "interface/enterFullscreenPreview",
      ),
    exitFullscreenPreview: () =>
      set(
        { isFullscreenPreview: false },
        undefined,
        "interface/exitFullscreenPreview",
      ),
    toggleCurrentCanvasTheme: (theme: SiteTheme) =>
      set(
        { currentCanvasTheme: theme },
        undefined,
        "interface/toggleCurrentCanvasTheme",
      ),
    inlineEditingBlockPath: initialData.inlineEditingBlockPath ?? null,
    isActionSidebarOpen: false,
    isInlineEditingMode: initialData.isInlineEditingMode ?? false,
    isRightSidebarOpen: false,
    isDragging: false,
    isDraggingEnabled: true,
    isFullscreenPreview: false,
    isSortingEnabled: false,
    toggleAppearance: (appearance: Appearance) =>
      set({ appearance }, undefined, "interface/toggleAppearance"),
    setCanvasSize: canvasSize =>
      set({ canvasSize }, undefined, "interface/setCanvasSize"),
    setDropZonePositions: positions =>
      set(
        { dropZonePositions: positions },
        undefined,
        "interface/setDropZonePositions",
      ),
    setIsDragging: isDragging =>
      set({ isDragging }, undefined, "interface/setIsDragging"),
    setInlineEditingBlockPath: path =>
      set(
        { inlineEditingBlockPath: path },
        undefined,
        "interface/setInlineEditingBlockPath",
      ),
    toggleInlineEditingMode: () =>
      set(
        state => ({ isInlineEditingMode: !state.isInlineEditingMode }),
        undefined,
        "interface/toggleInlineEditingMode",
      ),
    isInlineEditing: (blockPath: string) => {
      const { isInlineEditingMode, inlineEditingBlockPath } = get();
      return isInlineEditingMode && inlineEditingBlockPath === blockPath;
    },
    startInlineEditing: (blockPath: string) => {
      const {
        setSelectedBlockPath,
        isInlineEditingMode,
        toggleInlineEditingMode,
        setInlineEditingBlockPath,
      } = get();
      setSelectedBlockPath(blockPath);

      if (!isInlineEditingMode) {
        toggleInlineEditingMode();
      }

      setInlineEditingBlockPath(blockPath);
    },
    stopInlineEditing: () => {
      const {
        setInlineEditingBlockPath,
        isInlineEditingMode,
        toggleInlineEditingMode,
      } = get();

      setInlineEditingBlockPath(null);
      if (isInlineEditingMode) {
        toggleInlineEditingMode();
      }
    },
    commitInlineEdit: (newText: string) => {
      const { getSelectedBlock, updateSelectedBlock, stopInlineEditing } =
        get();

      const selectedBlockData = getSelectedBlock();
      if (selectedBlockData?.block) {
        const updatedBlock = structuredClone(selectedBlockData.block);

        if (updatedBlock.props.text) {
          updatedBlock.props.text.value = newText;
        }

        updateSelectedBlock(updatedBlock);
      }

      stopInlineEditing();
    },
    toggleRightSidebar: () =>
      set(
        state => ({ isRightSidebarOpen: !state.isRightSidebarOpen }),
        undefined,
        "interface/toggleRightSidebar",
      ),

    ...createThemeUpdater(set, get),
    zoom: createZoomSlice(set, get),

    toggleSidebarView: view => {
      const { actionSidebarView, isActionSidebarOpen } = get();
      const isSameView = view === actionSidebarView && isActionSidebarOpen;

      set(
        {
          actionSidebarView: isSameView ? undefined : view,
          isActionSidebarOpen: !isSameView,
        },
        undefined,
        "interface/toggleSidebarView",
      );
    },
  });

const isValidThemeUpdate = (
  theme: SiteTheme | null,
  key: string,
): theme is SiteTheme => {
  if (!theme) {
    console.warn("No currentCanvasTheme is set.");
    return false;
  }

  if (!theme.cssVariables) {
    console.warn("Theme has no cssVariables defined.");
    return false;
  }

  if (!(key in theme.cssVariables)) {
    console.warn(`No CSS variable group found for key: ${key}`);
    return false;
  }

  return true;
};
